import { InputHelpMessagePosition, InputState, LABEL_TYPE, } from '@kubit/web-ui-components/input/types';
import { DeviceBreakpointsType } from '@kubit/web-ui-components/types/breakpoints';
import { FONT_WEIGHT, TEXT_ALIGN } from '../../../foundations/typography';
import { TextVariantType } from '../../variants';
const buildInputCommonProps = (FOUNDATIONS, FONT) => ({
    topContentContainer: {
        margin_bottom: FOUNDATIONS.spacing_150,
    },
    inputWrapperContainer: {
        display: 'flex',
        flex_direction: 'column',
    },
    focusVisibleCustom: {
        border_color: 'transparent',
        outline: `${FOUNDATIONS.color_accent_active_primary_border_50} solid ${FOUNDATIONS.border_100}`,
        outline_offset: 'unset',
        box_shadow: 'none',
    },
    input: {
        font_weight: FONT_WEIGHT.font_regular,
        font_size: FONT.font_paragraph_200[DeviceBreakpointsType.DESKTOP].font_size,
        line_height: FONT.font_paragraph_200[DeviceBreakpointsType.DESKTOP].line_height,
        color: FOUNDATIONS.color_exception_neutral_font_350,
    },
    inputContainer: {
        opacity: '1',
        padding: FOUNDATIONS.spacing_200,
        background_color: FOUNDATIONS.color_neutral_bg_mainsection_50,
        border_width: FOUNDATIONS.border_50,
        border_color: FOUNDATIONS.color_neutral_border_mainsection_100,
        border_radius: FOUNDATIONS.radius_200,
        border_style: 'solid',
    },
    placeholder: {
        font_variant: FONT.font_paragraph_200[DeviceBreakpointsType.DESKTOP].font_size,
        font_weight: FONT_WEIGHT.font_regular,
        color: FOUNDATIONS.color_neutral_font_mainsection_50,
    },
    errorAndHelpMessageContainer: {
        display: 'flex',
        flex_direction: 'column',
        margin_top: FOUNDATIONS.spacing_150,
    },
    messagesAndCounterContainer: {
        align_items: 'flex-end',
        gap: FOUNDATIONS.spacing_150,
    },
    helpMessage: {
        text_align: TEXT_ALIGN.left,
        font_weight: FONT_WEIGHT.font_regular,
        color: FOUNDATIONS.color_neutral_font_mainsection_50,
        font_variant: TextVariantType.FONT_PARAGRAPH_350,
        position: InputHelpMessagePosition.BOTTOM,
    },
    helpMessageContainer: {
        padding: FOUNDATIONS.spacing_00,
        padding_left: FOUNDATIONS.spacing_00,
        display: 'flex',
        flex_direction: 'row',
        justify_content: 'space-between',
    },
    inputIcon: {
        color: FOUNDATIONS.color_comp_actions_iconbutton_secondary_default_icon,
        width: FOUNDATIONS.size_300,
        height: FOUNDATIONS.size_300,
    },
    inputIconContainer: {
        position: 'absolute',
        top: '50%',
        transform: 'translate(0%, -50%)',
        left: FOUNDATIONS.spacing_200,
    },
    inputIconContainerRight: {
        position: 'absolute',
        top: '50%',
        transform: 'translate(0%, -50%)',
        left: 'auto',
        right: FOUNDATIONS.spacing_200,
    },
    label: {
        type: LABEL_TYPE.STANDARD,
        font_weight: FONT_WEIGHT.font_regular,
        font_variant: TextVariantType.FONT_PARAGRAPH_200,
        color: FOUNDATIONS.color_exception_neutral_font_350,
    },
    additionalInfoContainer: {
        display: 'flex',
        flex: '1',
        justify_content: 'flex-start',
        margin_left: FOUNDATIONS.spacing_100,
    },
    loaderContainer: {
        position: 'absolute',
        top: '50%',
        left: 'auto',
        right: '1.1rem',
        transform: 'translate(0%, -50%)',
    },
});
const buildInputErrorCommonProps = (FOUNDATIONS, FONT) => {
    const inputCommonProps = buildInputCommonProps(FOUNDATIONS, FONT);
    return {
        inputContainer: {
            ...inputCommonProps.inputContainer,
            border_width: FOUNDATIONS.border_100,
            border_color: 'transparent',
            outline: `${FOUNDATIONS.color_feedback_error_border_mainsection_50} solid ${FOUNDATIONS.border_100}`,
            background_color: FOUNDATIONS.color_feedback_error_bg_mainsection_50,
        },
        errorAndHelpMessageContainer: {
            ...inputCommonProps.errorAndHelpMessageContainer,
            gap: FOUNDATIONS.spacing_100,
        },
        errorMessage: {
            font_variant: TextVariantType.FONT_PARAGRAPH_350,
            font_weight: FONT_WEIGHT.font_regular,
            font_align: TEXT_ALIGN.left,
            color: FOUNDATIONS.color_feedback_error_font_mainsection_50,
        },
        errorMessageContainer: {
            display: 'flex',
            flex_direction: 'row',
            gap: FOUNDATIONS.spacing_100,
            padding_left: FOUNDATIONS.spacing_00,
        },
        errorMessageIcon: {
            width: FOUNDATIONS.size_250,
            height: FOUNDATIONS.size_250,
        },
        focusVisibleCustom: {
            outline_offset: 'unset',
            box_shadow: 'none',
            border_color: 'transparent',
            outline: `${FOUNDATIONS.color_feedback_error_border_mainsection_50} solid ${FOUNDATIONS.border_100}`,
        },
    };
};
const buildInputDisabledCommonProps = (FOUNDATIONS, FONT) => {
    const inputCommonProps = buildInputCommonProps(FOUNDATIONS, FONT);
    return {
        inputContainer: {
            ...inputCommonProps.inputContainer,
            background_color: FOUNDATIONS.color_accent_disabled_bg_mainsection_100,
            border_width: FOUNDATIONS.border_50,
            border_color: FOUNDATIONS.color_accent_disabled_border_100,
        },
        input: {
            color: FOUNDATIONS.color_accent_disabled_font_mainsection_100,
        },
        label: {
            ...inputCommonProps.label,
            color: FOUNDATIONS.color_accent_disabled_font_mainsection_100,
        },
        placeholder: {
            ...inputCommonProps.placeholder,
            color: FOUNDATIONS.color_accent_disabled_font_mainsection_100,
        },
        helpMessage: {
            ...inputCommonProps.helpMessage,
            color: FOUNDATIONS.color_accent_disabled_font_mainsection_100,
        },
        inputIcon: {
            ...inputCommonProps.inputIcon,
            color: FOUNDATIONS.color_accent_disabled_icon_mainsection_100,
        },
    };
};
export const buildInputMainSectionStyles = (FOUNDATIONS, FONT) => {
    const inputCommonProps = buildInputCommonProps(FOUNDATIONS, FONT);
    const errorCommonProps = buildInputErrorCommonProps(FOUNDATIONS, FONT);
    const disabledCommonProps = buildInputDisabledCommonProps(FOUNDATIONS, FONT);
    return {
        [InputState.EMPTY]: {
            ...inputCommonProps,
        },
        [InputState.ACTIVE]: {
            ...inputCommonProps,
            inputContainer: {
                ...inputCommonProps.inputContainer,
                outline_width: FOUNDATIONS.border_100,
                outline_offset: FOUNDATIONS.border_100,
                outline_style: 'solid',
                outline_color: FOUNDATIONS.color_accent_active_primary_border_mainsection_50,
            },
            placeholder: {
                ...inputCommonProps.placeholder,
                color: FOUNDATIONS.color_neutral_font_50,
            },
        },
        [InputState.FILLED]: {
            ...inputCommonProps,
        },
        [InputState.ERROR_EMPTY]: {
            ...inputCommonProps,
            ...errorCommonProps,
            inputContainer: {
                ...errorCommonProps.inputContainer,
                background_color: FOUNDATIONS.color_feedback_error_bg_100,
            },
        },
        [InputState.ERROR_ACTIVE]: {
            ...inputCommonProps,
            ...errorCommonProps,
            inputContainer: {
                ...errorCommonProps.inputContainer,
                background_color: FOUNDATIONS.color_feedback_error_bg_200,
            },
        },
        [InputState.ERROR_FILLED]: {
            ...inputCommonProps,
            ...errorCommonProps,
            inputContainer: {
                ...errorCommonProps.inputContainer,
                background_color: FOUNDATIONS.color_feedback_error_bg_200,
            },
        },
        [InputState.DISABLED_FILLED]: {
            ...inputCommonProps,
            ...disabledCommonProps,
            label: {
                ...disabledCommonProps.label,
                color: FOUNDATIONS.color_accent_disabled_font_mainsection_50,
            },
            input: {
                ...disabledCommonProps.input,
                color: FOUNDATIONS.color_accent_disabled_font_mainsection_50,
            },
            helpMessage: {
                ...disabledCommonProps.helpMessage,
                color: FOUNDATIONS.color_accent_disabled_font_mainsection_50,
            },
            inputIcon: {
                ...disabledCommonProps.inputIcon,
                color: FOUNDATIONS.color_accent_disabled_icon_mainsection_50,
            },
        },
        [InputState.DISABLED_EMPTY]: {
            ...inputCommonProps,
            ...disabledCommonProps,
        },
    };
};
//# sourceMappingURL=mainSection.js.map